/**
 * A reading link's secrets on the device (part 38d log, DP1, DP2): the fragment key and the owner secret made at random,
 * and what derives from them: the owner proof and the link's ID (crypto's `ownerProof`, `linkIdOf`), the record's ID (the
 * link ID's bytes in hex) and the link a reader opens, whose fragment alone holds the key. WebCrypto and the randomness are
 * injected.
 */
import { linkIdOf, OWNER_SECRET_BYTES, ownerProof, SHARE_KEY_BYTES } from '@cockatrice/crypto';
import { toHex } from '@cockatrice/core';
import type { ShareLinkRecord } from '@cockatrice/storage';
import { toBase64Url } from '../account/bytes.ts';

/** The link's secrets and what derives from them. */
export interface LinkSecrets {
  readonly key: Uint8Array<ArrayBuffer>;
  readonly secret: Uint8Array<ArrayBuffer>;
  readonly proof: Uint8Array<ArrayBuffer>;
  /** The link's ID's bytes. */
  readonly id: Uint8Array<ArrayBuffer>;
  /** The link's ID as the relay names it: base64url. */
  readonly link: string;
}

/** Random bytes of a length (`crypto.getRandomValues` in the app). */
export type Random = (length: number) => Uint8Array<ArrayBuffer>;

/** Random bytes of a length from a filler of arrays (`crypto.getRandomValues`). */
export function randomOf(fill: (bytes: Uint8Array<ArrayBuffer>) => unknown): Random {
  return (length) => {
    const bytes = new Uint8Array(length);
    fill(bytes);
    return bytes;
  };
}

/** `hex` (lowercase, two digits a byte, as the record holds it) as bytes. */
export function bytesOfHex(hex: string): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(hex.length / 2);
  for (let index = 0; index < bytes.length; index += 1) bytes[index] = Number.parseInt(hex.slice(2 * index, 2 * index + 2), 16);
  return bytes;
}

/** DP2: what `key` and `secret` give. */
async function derived(subtle: SubtleCrypto, key: Uint8Array<ArrayBuffer>, secret: Uint8Array<ArrayBuffer>): Promise<LinkSecrets> {
  const proof = await ownerProof(subtle, secret);
  const id = await linkIdOf(subtle, proof);
  return { key, secret, proof, id, link: toBase64Url(id) };
}

/** DP1: a new link's secrets: a fresh key and a fresh owner secret. */
export function newLinkSecrets(subtle: SubtleCrypto, random: Random): Promise<LinkSecrets> {
  return derived(subtle, random(SHARE_KEY_BYTES), random(OWNER_SECRET_BYTES));
}

/** The secrets a record holds (its key and secret, read by the store's reader as hex of their lengths). */
export function secretsOf(subtle: SubtleCrypto, record: Pick<ShareLinkRecord, 'key' | 'secret'>): Promise<LinkSecrets> {
  return derived(subtle, bytesOfHex(record.key), bytesOfHex(record.secret));
}

/** The record's ID for a link of `secrets`: its ID's bytes in hex. */
export function recordIdOf(secrets: LinkSecrets): string {
  return toHex(secrets.id);
}

/**
 * DP2, LW12: the link a reader opens at the reader page's `origin`: the link's ID in the path, the key in the fragment,
 * which a browser never sends.
 */
export function readerUrl(origin: string, secrets: Pick<LinkSecrets, 'link' | 'key'>): string {
  return `${origin}/r/${secrets.link}#${toBase64Url(secrets.key)}`;
}
